import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Loader2, Users, Target, Sparkles, CheckCircle2, 
  AlertCircle, UserPlus, ArrowRight, Brain
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const InviteLanding = () => {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    fetchInvitation();
  }, [inviteCode]);

  const fetchInvitation = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/invite/${inviteCode}`);
      
      if (response.data.success) {
        setInvitation(response.data.invitation);
      } else {
        setError(response.data.message || 'Invalid invitation');
      }
    } catch (err) {
      console.error('Error fetching invitation:', err);
      setError(err.response?.data?.detail || 'Invitation not found');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!isAuthenticated) {
      // Store invite code and redirect to signup
      localStorage.setItem('pending_invite_code', inviteCode);
      localStorage.setItem('redirect_after_auth', `/invite/${inviteCode}`);
      navigate('/auth?mode=signup&invite=' + inviteCode);
      return;
    }

    try {
      setAccepting(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/invite/${inviteCode}/accept`,
        {},
        { withCredentials: true }
      );

      if (response.data.success) {
        setAccepted(true);
        // Redirect to Impact Lab after a moment
        setTimeout(() => {
          navigate(response.data.redirect_url || `/challenge/${invitation.challenge_id}`);
        }, 2000);
      }
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError(err.response?.data?.detail || 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full mx-auto mb-6 flex items-center justify-center animate-bounce">
              <CheckCircle2 className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold mb-2 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Welcome, Fountainhead! 🎉
            </h1>
            <p className="text-slate-600 mb-4">
              You've joined the team! Redirecting you to the challenge...
            </p>
            <Loader2 className="w-6 h-6 animate-spin text-purple-600 mx-auto" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-20 h-20 bg-red-100 rounded-full mx-auto mb-6 flex items-center justify-center">
              <AlertCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold mb-2 text-slate-900">
              Invitation Issue
            </h1>
            <p className="text-slate-600 mb-6">{error}</p>
            <Button onClick={() => navigate('/challenges')} className="bg-purple-600 hover:bg-purple-700">
              Browse Challenges
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Header */}
      <header className="border-b bg-white/90 backdrop-blur-md">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <Brain className="w-8 h-8 text-purple-600" />
            <span className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Idea Fountain
            </span>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-6 py-12 max-w-2xl">
        <Card className="overflow-hidden">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 p-8 text-white text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full mx-auto mb-4 flex items-center justify-center">
              <UserPlus className="w-8 h-8" />
            </div>
            <h1 className="text-2xl font-bold mb-2">You've Been Invited!</h1>
            <p className="opacity-90">Join a team solving real-world challenges</p>
          </div>

          <CardContent className="p-8">
            {/* Invitation Message */}
            <div className="text-center mb-8">
              <p className="text-lg text-slate-700 mb-4">
                <strong className="text-purple-700">{invitation.inviter_name}</strong> admires your experience in{' '}
                <strong className="text-pink-600">{invitation.expertise_area}</strong>{' '}
                and is inviting you to help solve a social challenge.
              </p>
              
              {invitation.custom_message && (
                <div className="bg-slate-50 rounded-lg p-4 italic text-slate-600 border-l-4 border-purple-400">
                  "{invitation.custom_message}"
                </div>
              )}
            </div>

            {/* Challenge Card */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 mb-8 border-2 border-purple-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-slate-900 mb-1">
                    {invitation.challenge_title}
                  </h3>
                  <p className="text-sm text-slate-600">
                    7-Day IDEO Design Sprint • Team Collaboration • Real Impact
                  </p>
                </div>
              </div>
            </div>

            {/* What You'll Do */}
            <div className="mb-8">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                What You'll Do
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-lg p-3 border text-sm">
                  <Users className="w-4 h-4 text-purple-600 mb-1" />
                  <span className="text-slate-700">Collaborate with a diverse team</span>
                </div>
                <div className="bg-white rounded-lg p-3 border text-sm">
                  <Target className="w-4 h-4 text-pink-600 mb-1" />
                  <span className="text-slate-700">Tackle real social challenges</span>
                </div>
                <div className="bg-white rounded-lg p-3 border text-sm">
                  <Brain className="w-4 h-4 text-orange-600 mb-1" />
                  <span className="text-slate-700">Use AI-powered ideation tools</span>
                </div>
                <div className="bg-white rounded-lg p-3 border text-sm">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mb-1" />
                  <span className="text-slate-700">Create tangible solutions</span>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <Button 
              onClick={handleAccept}
              disabled={accepting}
              className="w-full bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 hover:from-purple-700 hover:via-pink-700 hover:to-orange-600 text-lg py-6"
            >
              {accepting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Joining...
                </>
              ) : isAuthenticated ? (
                <>
                  Accept Invitation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              ) : (
                <>
                  Sign Up & Accept
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>

            {!isAuthenticated && (
              <p className="text-center text-sm text-slate-500 mt-4">
                Already have an account?{' '}
                <button 
                  onClick={() => {
                    localStorage.setItem('pending_invite_code', inviteCode);
                    localStorage.setItem('redirect_after_auth', `/invite/${inviteCode}`);
                    navigate('/auth?invite=' + inviteCode);
                  }}
                  className="text-purple-600 hover:underline font-medium"
                >
                  Log in
                </button>
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InviteLanding;
